import { Box, Sheet, IconButton, Typography, Divider } from '@mui/joy'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import { getMaximizedCallOverlayAnimationStyles, maximizedUIAnimationStyles } from '../../../utils/styles'
import { CallControls, CallInfo, DTMFKeypad, LoadDetails } from '../../widget-subcomponents'
import { CallStatus } from '@numeo/types'
import { useRef } from 'react'
import { DTMFKeypadRef } from '../../widget-subcomponents/DTMFKeypad'
import { LoadRouteAndToll } from 'components/shared/LoadsTable/MapAndCosts'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from 'hooks'

interface MaximizedUserCallWidgetProps {
    callId: string
    onMinimize: () => void
    isMinimizing: boolean
    muted: boolean
    onToggleMute: () => Promise<void> | void
    onEndCall: () => Promise<void> | void
    sendDTMF: (digit: string) => Promise<void> | void
    endingCall: boolean
}
export const MaximizedUserCallWidget = ({
    callId,
    onMinimize,
    isMinimizing,
    muted,
    onToggleMute,
    onEndCall,
    sendDTMF,
    endingCall,
}: MaximizedUserCallWidgetProps) => {
    const [searchParams] = useSearchParams()
    const dtmfKeypadRef = useRef<DTMFKeypadRef>(null)
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeAvailableCall = activeAvailableCalls[callId]

    return (
        <>
            <Box sx={getMaximizedCallOverlayAnimationStyles(isMinimizing)} onClick={onMinimize} />
            <Sheet
                sx={{
                    ...maximizedUIAnimationStyles,
                    animation: 'expandFromBottom 0.3s ease-out forwards',
                    border: '1px solid',
                    borderColor: 'divider',
                    pl: 2,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        opacity: isMinimizing ? 0.5 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                >
                    <IconButton
                        onClick={onMinimize}
                        variant="plain"
                        sx={{
                            mr: 1,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                backgroundColor: 'primary.50',
                            },
                        }}
                    >
                        <CloseFullscreenIcon />
                    </IconButton>
                    <Typography
                        level="h4"
                        sx={{
                            ml: 1,
                            color: 'success.500',
                            opacity: isMinimizing ? 0 : 1,
                            transform: isMinimizing ? 'translateX(-10px)' : 'translateX(0)',
                            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                        }}
                    >
                        Call
                    </Typography>
                </Box>

                {/* Main Content */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', maxWidth: '100%', mt: 1 }}>
                    <>
                        {/* Left Column - Call Info & Controls */}
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                mt: 1,
                            }}
                        >
                            <CallInfo
                                to={activeAvailableCall.phoneNumber}
                                from={activeAvailableCall.call?.from || 'Loading...'}
                                call={activeAvailableCall.call}
                            />

                            <CallControls
                                callStatus={activeAvailableCall.call?.status || CallStatus.INITIATING}
                                isMuted={muted}
                                endingCall={endingCall}
                                onToggleMute={onToggleMute}
                                onHangup={onEndCall}
                            />
                        </Box>

                        {/* Right Column - DTMF Keypad */}
                        <Box sx={{ mt: 2 }}>
                            <DTMFKeypad ref={dtmfKeypadRef} callStatus={activeAvailableCall.call?.status || CallStatus.INITIATING} sendDTMF={sendDTMF} />
                        </Box>
                    </>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Load Details */}
                <LoadDetails load={activeAvailableCall.load} dtmfKeypadRef={dtmfKeypadRef} />

                <Divider sx={{ my: 2 }} />

                {/* Map and Costs */}
                <LoadRouteAndToll
                    load={activeAvailableCall.load}
                    direction="column"
                    truckId={searchParams.get('truckId') || '6840555d33c97c9a98a43702'}
                    variant="default"
                />
            </Sheet>
        </>
    )
}
